/**
 * Tips: what the guest can choose, and how the server reports the result.
 *
 * The guest only ever chooses — no tip, one of the venue's percentages, or a fixed amount.
 * The server works out the money (a percentage of the items after any discount), puts it
 * on the order and includes it in `total`. Nothing here computes a tip.
 */

/** The tip on an order, as the server reported it. */
export interface OrderTip {
  amount: number;
  percent: number | null;
  fixedAmount: number | null;
}

export type TipChoice =
  | { kind: 'none' }
  | { kind: 'percent'; percent: number }
  | { kind: 'fixed'; amount: number };

/** The venue's tip options, from the shop payload's `tips` key. */
export interface TipSettings {
  enabled: boolean;
  presets: number[];
  allowCustom: boolean;
  maxAmount: number | null;
}

const TIPS_OFF: TipSettings = { enabled: false, presets: [], allowCustom: false, maxAmount: null };

/** Read `clientData.tips`. An older backend sends no key at all, which is simply "off". */
export function parseTipSettings(clientData: any): TipSettings {
  const tips = clientData?.tips;
  if (!tips || typeof tips !== 'object' || !tips.enabled) {
    return TIPS_OFF;
  }

  const presets = Array.isArray(tips.presets)
    ? tips.presets.map((p: any) => Number(p)).filter((p: number) => isFinite(p) && p > 0)
    : [];
  const max = Number(tips.max_amount);

  return {
    enabled: true,
    presets,
    allowCustom: !!tips.allow_custom,
    maxAmount: isFinite(max) && max > 0 ? max : null,
  };
}

/**
 * The tip on an order response.
 *
 * Two shapes, both from the server: the `feather_extras.tip` block an order create/update
 * answers with, and the fee line itself (meta `_feather_extra = tip`), which is all a plain
 * GET of a WooCommerce order carries. The block wins when present.
 */
export function parseOrderTip(order: any): OrderTip | null {
  if (!order || typeof order !== 'object') return null;

  const extras = order.feather_extras;
  if (extras && typeof extras === 'object' && 'tip' in extras) {
    const tip = extras.tip;
    const amount = parseFloat(tip?.amount);
    if (!tip || isNaN(amount) || amount <= 0) return null;
    return {
      amount,
      percent: numberOrNull(tip.percent),
      fixedAmount: numberOrNull(tip.fixed_amount),
    };
  }

  const line = (Array.isArray(order.fee_lines) ? order.fee_lines : []).find((l: any) =>
    metaValue(l?.meta_data, '_feather_extra') === 'tip'
  );
  const amount = parseFloat(line?.total);
  if (!line || isNaN(amount) || amount <= 0) return null;

  return {
    amount,
    percent: numberOrNull(metaValue(line.meta_data, '_feather_tip_percent')),
    fixedAmount: numberOrNull(metaValue(line.meta_data, '_feather_tip_fixed')),
  };
}

/** The choice a reported tip represents — what the picker should show as selected. */
export function tipChoiceOf(tip: OrderTip | null): TipChoice {
  if (!tip) return { kind: 'none' };
  if (tip.percent !== null) return { kind: 'percent', percent: tip.percent };
  return { kind: 'fixed', amount: tip.fixedAmount ?? tip.amount };
}

/** The same choice? Two "No tip"s are; percentages and amounts compare by value. */
export function sameTipChoice(a: TipChoice, b: TipChoice): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'percent' && b.kind === 'percent') return a.percent === b.percent;
  if (a.kind === 'fixed' && b.kind === 'fixed') return a.amount === b.amount;
  return true;
}

/**
 * Tips are for online payment only: the server offers them only to venues that take online
 * payment, and drops the tip from any order paid another way. So the picker is shown only
 * while online payment is the method chosen.
 */
export const TIP_PAYMENT_METHOD = 'online_payment';

function metaValue(meta: any, key: string): any {
  if (!Array.isArray(meta)) return undefined;
  return meta.find((m: any) => m?.key === key)?.value;
}

function numberOrNull(value: any): number | null {
  const n = parseFloat(value);
  return isNaN(n) || n <= 0 ? null : n;
}
